import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CalendarPicker from '../util/CalendarPicker';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import { transformToHierarchy } from '../util/tree';
import TreeViewComponent from '../util/TreeViewComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import { fxSelectStyles } from './AddExpenses';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';

interface TemporaryDrawerProps {
  setShowModalAdd: any,
  is24HourFormat: any
  refreshExpenses: any,
  cars: any,
  refreshCars: any,
  unit: any,
  access?: any,
}

export default function AddExpensesCar({ setShowModalAdd, is24HourFormat, refreshExpenses, cars, refreshCars, unit, access }: TemporaryDrawerProps) {
  const [usersById, setUsersById] = useState<Map<number, any>>(new Map());
  const canForOthers = access?.finance_create_for_others === true || access?.finance_create_for_others === 1;
  const [isCategoryListVisible, setCategoryListVisible] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [allCategory, setAllCategory] = useState<any[]>([]);
  const [categoryName, setCategoryName] = useState<any[]>([]);
  const transformToHierarchyCallback = useCallback(transformToHierarchy, []);
  const [prevSelectedCategoryId, setPrevSelectedCategoryId] = useState<number[]>([]);
  const [category, setCategory] = useState<any[]>([]);
  const [openCheckModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number[]>([]);
  const [destination, setDestination] = useState('');
  const [startPoint, setStartPoint] = useState('');
  const [car, setCar] = useState('');
  const [distanceRate1, setDistanceRate1] = useState('');
  const [distanceRate2, setDistanceRate2] = useState('');
  const [companyRates, setCompanyRates] = useState<{ r1Label: string; r1Price: number; r2Label: string; r2Price: number } | null>(null);
  const [purpose, setPurpose] = useState<string>('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState<Date | null>(null);
  const [drivableCars, setDrivableCars] = useState<any[]>([]);
  const [driver, setDriver] = useState<string>('');
  const [meId, setMeId] = useState<number | null>(null);
  const handleClose = () => setShowModalAdd(false);
  const { enqueueSnackbar } = useSnackbar();

  const selectedCar = drivableCars.find((c: any) => String(c.id) === car);
  const driverOptions: any[] = selectedCar?.drivers || [];

  const handleCarChange = (val: string) => {
    setCar(val);
    const c = drivableCars.find((x: any) => String(x.id) === val);
    const opts: any[] = c?.drivers || [];
    if (meId && opts.some((o) => o.id === meId)) setDriver(String(meId));
    else if (opts.length) setDriver(String(opts[0].id));
    else setDriver('');
  };
  const d1 = Number(distanceRate1) || 0;
  const d2 = Number(distanceRate2) || 0;
  const r1 = companyRates?.r1Price ?? 0;
  const r2 = companyRates?.r2Price ?? 0;
  const estimatedCost = (d1 > 0 || d2 > 0) ? (d1 * r1 + d2 * r2).toFixed(2) : null;

  const today = new Date();
  const licenseExpired = licenseExpiry && licenseExpiry < today;
  const licenseExpiringSoon = licenseExpiry && !licenseExpired &&
    (licenseExpiry.getTime() - today.getTime()) < 30 * 24 * 60 * 60 * 1000;

  const handleCategoryListClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleDateChange = (date: Date) => {
    setStartDate(date);
  };

  const handleNodeSelect = (nodeId: number) => {
    setSelectedCategoryId([nodeId])
  };

  const handleClickCategoryBox = (event: React.MouseEvent) => {
    setCategoryListVisible(!isCategoryListVisible);
    if (isCategoryListVisible) {
      setSelectedCategoryId(prevSelectedCategoryId)
    }
    setPrevSelectedCategoryId(selectedCategoryId);
    event.stopPropagation();
  };

  const handleClickAddCategoryId = (selectedId: number) => {
    const selectedCategory = allCategory?.find(node => node.id === selectedId);
    if (selectedCategory) {
      setCategoryName(selectedCategory.name)
      setCategoryListVisible(false);
    } else {
      setCategoryName(['All category'])
      console.log('Category not found');
      setCategoryListVisible(false);
    }
  };

  const handleExpenses = async () => {
    try {
      if (selectedCategoryId.length === 0) {
        enqueueSnackbar(t('ChooseCategory'), { variant: 'error' });
        return;
      }

      if (car === '') {
        enqueueSnackbar(t('ChooseCars'), { variant: 'error' });
        return;
      }

      // BUG-3: validate all required fields
      if (!startPoint.trim()) {
        enqueueSnackbar(t('StartingPointRequired'), { variant: 'error' });
        return;
      }

      if (!destination.trim()) {
        enqueueSnackbar(t('DestinationRequired'), { variant: 'error' });
        return;
      }

      if (!purpose.trim()) {
        enqueueSnackbar(t('PurposeRequired'), { variant: 'error' });
        return;
      }

      if (d1 <= 0 && d2 <= 0) {
        enqueueSnackbar(t('DistanceRequired'), { variant: 'error' });
        return;
      }

      const payload: any = {
        date: startDate,
        categoryId: Number(selectedCategoryId[0]),
        carsId: Number(car),
        destination,
        starting_point: startPoint,
        purpose,
        distance: d1 + d2,
        distance_rate1: d1,
        distance_rate2: d2,
        status: 'pending',
        additional_info: additionalInfo,
        userId: driver ? Number(driver) : undefined,
      };

      await axios.post(`cars-expenses/add`, payload);

      handleClose();
      refreshExpenses();
      enqueueSnackbar(t('SavedSuccessfully'), { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error creating', { variant: 'error' });
      console.error('Error creating:', error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: categoryData } = await axios.get('category');
        setAllCategory(categoryData)
        const hierarchyData = transformToHierarchyCallback(categoryData);
        setCategory(hierarchyData);
        const { data: driv } = await axios.get('cars/drivable');
        const drivList = Array.isArray(driv) ? driv : [];
        setDrivableCars(drivList);
        try {
          const { data: us } = await axios.get('users');
          setUsersById(new Map((Array.isArray(us) ? us : []).map((u: any) => [u.id, u])));
        } catch { /* interceptor */ }
        const { data: userData } = await axios.get('user');
        const uid = userData?.id ?? null;
        setMeId(uid);
        if (userData?.license_expiry_date) {
          setLicenseExpiry(new Date(userData.license_expiry_date));
        }
        const co = userData?.company;
        if (co) {
          setCompanyRates({
            r1Label: co.car_rate1_label || 'Rate 1',
            r1Price: Number(co.car_rate1_price ?? 0),
            r2Label: co.car_rate2_label || 'Rate 2',
            r2Price: Number(co.car_rate2_price ?? 0),
          });
        }
        // авто по умолчанию, если доступно текущему пользователю
        const defId = userData?.default_car_id;
        let initialCar = '';
        if (defId && drivList.some((c: any) => c.id === defId)) initialCar = String(defId);
        else if (drivList.length === 1) initialCar = String(drivList[0].id);
        if (initialCar) {
          setCar(initialCar);
          const c = drivList.find((x: any) => String(x.id) === initialCar);
          const opts: any[] = c?.drivers || [];
          if (uid && opts.some((o) => o.id === uid)) setDriver(String(uid));
          else if (opts.length) setDriver(String(opts[0].id));
        } else if (uid) {
          setDriver(String(uid));
        }
      } catch (e) {
      }
    })();
  }, [transformToHierarchyCallback]);

  const carOptions = drivableCars.map((c: any) => ({ value: c.id, label: `${c.brand} ${c.model} (${c.plate_number})` }));
  const driverRsOptions = driverOptions.map((d: any) => ({ value: d.id, label: `${d.first_name} ${d.last_name}${meId === d.id ? ` (${t('You') || 'you'})` : ''}` }));

  return (
    <div className='fx-modal' role="presentation">
      {/* Header */}
      <div className='fx-head'>
        <div className='fx-head-text'>
          <div className='fx-head-title'>{t('AddCarExpenses')}</div>
          <div className='fx-head-sub'>{t('AddCarExpenseSub')}</div>
        </div>
        <div className='fx-close' onClick={handleClose}><CloseIcon fontSize="small" /></div>
      </div>

      {/* Body */}
      <div className='fx-body'>

        {/* License warnings — own color scheme, not changed */}
        {licenseExpired && (
          <div className='px-[10px] py-[6px] rounded-[5px] text-[12px] font-semibold bg-red-100 text-red-700 border border-red-300'>
            {t('LicenseExpired')}: {licenseExpiry!.toLocaleDateString()}
          </div>
        )}
        {licenseExpiringSoon && (
          <div className='px-[10px] py-[6px] rounded-[5px] text-[12px] font-semibold bg-orange-100 text-orange-700 border border-orange-300'>
            {t('LicenseExpiringSoon')}: {licenseExpiry!.toLocaleDateString()}
          </div>
        )}

        {/* Date + Purpose */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <CalendarPicker
              mode="single"
              variant="bare"
              className="fx-input"
              value={startDate}
              onChange={(date: any) => handleDateChange(date)}
              displayFormat="DD/MM/YYYY"
            />
          </div>
          <div className='fx-field'>
            <input
              type="text"
              value={purpose}
              title={t('Purpose')}
              placeholder={`${t('Purpose')} *`}
              className="fx-input"
              onChange={(e: any) => setPurpose(e.target.value)}
            />
          </div>
        </div>

        {/* Starting Point + Destination */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <input
              type="text"
              value={startPoint}
              title={t('StartingPoint')}
              placeholder={`${t('StartingPoint')} *`}
              className="fx-input"
              onChange={(e: any) => setStartPoint(e.target.value)}
            />
          </div>
          <div className='fx-field'>
            <input
              type="text"
              value={destination}
              title={t('Destination')}
              placeholder={`${t('Destination')} *`}
              className="fx-input"
              onChange={(e: any) => setDestination(e.target.value)}
            />
          </div>
        </div>

        {/* Cars */}
        <div className='fx-field'>
          <Select
            classNamePrefix="app-rs"
            menuShouldScrollIntoView={false}
            menuPlacement="auto"
            options={carOptions}
            value={carOptions.find((o: any) => String(o.value) === String(car)) || null}
            onChange={(v: any) => handleCarChange(v ? String(v.value) : '')}
            placeholder={`${t('Cars')} *`}
            styles={fxSelectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            isClearable
          />
        </div>

        {/* Distance: Rate 1 + Rate 2 */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <span className='text-[10px] font-semibold uppercase tracking-[.04em] text-[var(--text-color-calendar-week)] mb-[4px] block'>
              {companyRates?.r1Label || 'Rate 1'} {companyRates?.r1Price ? `(${companyRates.r1Price}/unit)` : ''}
            </span>
            <input
              type="text"
              value={distanceRate1}
              placeholder="0"
              className="fx-input"
              onChange={(e: any) => {
                let v = e.target.value.replace(/[^0-9.]/g, '');
                if ((v.match(/\./g) || []).length > 1) v = v.slice(0, -1);
                setDistanceRate1(v);
              }}
            />
          </div>
          <div className='fx-field'>
            <span className='text-[10px] font-semibold uppercase tracking-[.04em] text-[var(--text-color-calendar-week)] mb-[4px] block'>
              {companyRates?.r2Label || 'Rate 2'} {companyRates?.r2Price ? `(${companyRates.r2Price}/unit)` : ''}
            </span>
            <input
              type="text"
              value={distanceRate2}
              placeholder="0"
              className="fx-input"
              onChange={(e: any) => {
                let v = e.target.value.replace(/[^0-9.]/g, '');
                if ((v.match(/\./g) || []).length > 1) v = v.slice(0, -1);
                setDistanceRate2(v);
              }}
            />
          </div>
        </div>

        {/* Driver (от кого) + Supervisor */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <span className='text-[10px] font-semibold uppercase tracking-[.04em] text-[var(--text-color-calendar-week)] mb-[4px] block'>{t('FromWhom') || 'From'}</span>
            <Select
              classNamePrefix="app-rs"
              menuShouldScrollIntoView={false}
              menuPlacement="auto"
              options={driverRsOptions}
              value={driverRsOptions.find((o: any) => String(o.value) === String(driver)) || null}
              onChange={(v: any) => setDriver(v ? String(v.value) : '')}
              isDisabled={!car || !canForOthers}
              placeholder={`${t('Driver')} *`}
              styles={fxSelectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            />
          </div>
          <div className='fx-field'>
            <span className='text-[10px] font-semibold uppercase tracking-[.04em] text-[var(--text-color-calendar-week)] mb-[4px] block'>{t('Supervisor') || 'Supervisor'}</span>
            <div className="fx-input" style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.75 }}>
              👤 {(() => {
                const drv = usersById.get(Number(driver));
                const sup = drv?.supervisorId != null ? usersById.get(drv.supervisorId) : null;
                return sup ? `${sup.first_name} ${sup.last_name}` : '—';
              })()}
            </div>
          </div>
        </div>

        {/* UX-4: live estimated cost */}
        {estimatedCost !== null && (
          <p className='text-[13px] text-[var(--text-color-calendar-week)]'>
            {t('EstimatedCost')}: <span className='font-semibold text-[var(--text-color)]'>{estimatedCost} {unit || 'km'}</span>
          </p>
        )}

        {/* Category */}
        <div className='fx-field'>
          <CategoryTaskPicker
            nodes={category}
            selectedCategoryId={selectedCategoryId}
            categoryName={categoryName}
            onCategorySelect={(id, name) => {
              if (id === -1) { setSelectedCategoryId([]); setCategoryName([]); }
              else { setSelectedCategoryId([id]); setCategoryName(name as any); }
            }}
            selectedTasks={[]}
            setSelectedTasks={() => {}}
            taskOrTasks={false}
            categoryOnly
            block
          />
        </div>

        {/* Additional info */}
        <div className='fx-field'>
          <textarea
            value={additionalInfo}
            title={t('AdditionalInfo')}
            placeholder={t('AdditionalInfo')}
            className="fx-textarea"
            rows={2}
            onChange={(e: any) => setAdditionalInfo(e.target.value)}
          />
        </div>
      </div>

      {/* Footer */}
      <div className='fx-foot'>
        <button
          type="button"
          className='fx-save'
          onClick={() => handleExpenses()}>
          {t('Create')}
        </button>
        <button
          type="button"
          className='fx-cancel'
          onClick={handleClose}>
          {t('Cancel')}
        </button>
      </div>
    </div>
  )
}
